import type { Customer } from "../types";

// Helper to generate realistic customer data
function generateMockCustomers(count: number): Customer[] {
  const customers: Customer[] = [];
  const now = Date.now();

  const firstNames = [
    "Sarah",
    "Michael",
    "Emma",
    "James",
    "Olivia",
    "William",
    "Sophia",
    "Robert",
    "Ava",
    "David",
    "Isabella",
    "John",
    "Mia",
    "Daniel",
    "Charlotte",
    "Joseph",
  ];

  const lastNames = [
    "Johnson",
    "Smith",
    "Williams",
    "Brown",
    "Jones",
    "Garcia",
    "Miller",
    "Davis",
    "Rodriguez",
    "Martinez",
    "Wilson",
    "Anderson",
    "Taylor",
    "Thomas",
    "Moore",
    "Martin",
  ];

  const companies = [
    "TechCorp",
    "Innovation Labs",
    "Digital Solutions",
    "Creative Agency",
    "Consulting Group",
    "Global Services",
    "StartUp Inc",
    "Enterprise Co",
  ];

  const domains = [
    "gmail.com",
    "outlook.com",
    "yahoo.com",
    "icloud.com",
    "protonmail.com",
  ];

  for (let i = 0; i < count; i++) {
    const timestamp = now - i * 7200000 - Math.random() * 7200000; // Spread over last few hours
    const firstName = firstNames[i % firstNames.length];
    const lastName = lastNames[i % lastNames.length];
    const displayName = `${firstName} ${lastName}`;
    const email = `${firstName.toLowerCase()}.${lastName.toLowerCase()}@${domains[i % domains.length]}`;
    const phone = `+1 ${String(Math.floor(Math.random() * 9000000000) + 1000000000).replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}`;
    const hasCompany = Math.random() > 0.4;

    customers.push({
      id: `customer_${i + 1}`, // Stable ID without timestamp
      client_id: "demo_client",
      display_name: displayName,
      given_name: firstName,
      family_name: lastName,
      middle_name: "",
      prefix: "",
      suffix: "",
      nickname: "",
      gender: Math.random() > 0.5 ? "M" : "F",
      addresses: {
        postal: [
          {
            full: `${Math.floor(Math.random() * 9999) + 1} ${lastNames[Math.floor(Math.random() * lastNames.length)]} Street, New York, NY 10001, USA`,
            street: `${Math.floor(Math.random() * 9999) + 1} ${lastNames[Math.floor(Math.random() * lastNames.length)]} Street`,
            city: "New York",
            region: "New York",
            postal_code: `100${String(Math.floor(Math.random() * 99) + 1).padStart(2, "0")}`,
            country: "USA",
            type: "home",
            primary: true,
          },
        ],
        email: [
          {
            address: email,
            type: hasCompany ? "work" : "home",
            primary: true,
          },
        ],
        phone: [
          {
            number: phone,
            type: "mobile",
            primary: true,
          },
        ],
      },
      organization: hasCompany
        ? {
            company: companies[i % companies.length],
            department: Math.random() > 0.5 ? "Sales" : "Marketing",
            title: Math.random() > 0.5 ? "Manager" : "Director",
          }
        : {
            company: undefined,
            department: undefined,
            title: undefined,
          },
      birthday: "",
      photo_url: "",
      notes: `Customer created from ${hasCompany ? "business" : "personal"} inquiry.`,
      additional_info: {},
      unverified_infos: {},
      relations: { contacts: [] },
      created_at: new Date(timestamp).getTime(),
      updated_at: new Date(timestamp).getTime(),
      created_by: "system",
      updated_by: "system",
      is_deleted: false,
    });
  }

  return customers;
}

// Generate a stable pool of customers once
const MOCK_CUSTOMERS_POOL = generateMockCustomers(50);

// Mock data for useCustomers (for recent customers table)
export const mockCustomersResult = {
  customers: MOCK_CUSTOMERS_POOL.slice(0, 50), // Return first 50
  total: 298,
};

// Mock data for useCustomer (single customer by ID)
export function getMockCustomer(customerId: string) {
  // Find customer in the stable pool
  const customer = MOCK_CUSTOMERS_POOL.find((c) => c.id === customerId);

  // If not found by exact ID, return the first customer as fallback
  return {
    customer: customer || MOCK_CUSTOMERS_POOL[0],
    callback_preferences: {
      next: Date.now() + 7 * 24 * 60 * 60 * 1000, // Next callback in 7 days
      rrule: "RRULE:FREQ=WEEKLY;BYDAY=MO;BYHOUR=10;BYMINUTE=0",
      text: "Weekly callback every Monday at 10:00 AM",
    },
  };
}
